
'use client'
import React, { useState, useEffect } from 'react'
import Image, { ImageProps } from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'

interface AnimatedImageProps extends Omit<ImageProps, 'src' | 'alt'> {
  thumbnailSrc: string
  mainSrc: string
  hoverSrc?: string
  alt: string
}

export default function AnimatedImage({ 
  thumbnailSrc, 
  mainSrc,
  hoverSrc,
  alt, 
  width, 
  height, 
  className, 
  ...props 
}: AnimatedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [showPlaceholder, setShowPlaceholder] = useState(true)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const img = new window.Image()
    img.src = mainSrc
    img.onload = () => {
      setIsLoaded(true)
      setShowPlaceholder(false)
    }

    // Preload hover image if it exists
    if (hoverSrc) {
      const hoverImg = new window.Image()
      hoverImg.src = hoverSrc
    }
  }, [mainSrc, hoverSrc])

  return (
    <div 
      className={`relative ${className || ''}`}
      style={{ width, height }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence>
        {showPlaceholder && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className="absolute inset-0 z-10"
          >
            <Image
              src={thumbnailSrc}
              alt={`${alt} thumbnail`}
              fill
              sizes={props.sizes}
              className="object-cover"
              style={{ filter: 'blur(10px)' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        className="absolute inset-0"
      >
        <AnimatePresence mode="wait">
          {isHovered && hoverSrc ? (
            <motion.div
              key="hover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0"
            >
              <Image
                src={hoverSrc}
                alt={`${alt} hover`}
                fill
                sizes={props.sizes}
                className="object-cover"
                {...props}
              />
            </motion.div>
          ) : (
            <motion.div
              key="main"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0"
            >
              <Image
                src={mainSrc}
                alt={alt}
                fill
                sizes={props.sizes}
                className="object-cover"
                {...props}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}